import { useMemo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Check } from 'lucide-react';
import { UserSelect } from '@/components/form/UserSelect';
import {
  cn,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@bliks-io/ui';
import type { UnresolvedDepartment, User, Department } from '@bliks-io/contracts';
import { PersonChip } from './PersonChip';
import { effectiveManagerId, managerEmailMatch } from '../utils/departmentManager';

// Unified type for display - can be either an unresolved department or a resolved ancestor
type DisplayDepartment =
  | { type: 'unresolved'; data: UnresolvedDepartment }
  | { type: 'resolved'; data: Department };

interface DepartmentResolutionTableProps {
  departments: UnresolvedDepartment[];
  resolvedDepartments?: Department[];
  managerAssignments: Map<string, string>;
  emailToUser: Map<string, User>;
  /** Personio manager suggestions by department id; never pre-selected */
  managerSuggestions?: Map<string, ManagerSuggestion>;
  onAssignmentChange: (deptId: string, managerId: string | undefined) => void;
  isSubmitting?: boolean;
}

/** A suggested manager the admin can take over with one click */
export interface ManagerSuggestion {
  displayName: string;
  user: User;
}

// managerAssignments distinguishes three states per department by key presence
// plus this sentinel: an absent key = untouched (fall back to the email match),
// this falsy sentinel = the user explicitly cleared the manager (stay
// unassigned, do NOT re-apply the match), any other value = the chosen manager.
// A falsy string is required because the map's value type is `string` (the
// shared ManagerResolutionStep boundary), so a real `null` can't be stored.
export const EXPLICIT_NO_MANAGER = '';

// Proportional column widths (was a CSS grid's fr track list) applied to the
// table-fixed header so the five columns keep the same ratios.
const COLUMN_WIDTHS = ['20%', '9.23%', '24.6%', '21.5%', '24.6%'];

// px-4.5 row edges + gap-3 between columns → 6px each side, 18px on the ends.
function cellPadding(index: number, total: number): string {
  if (index === 0) return 'pl-4.5 pr-1.5';
  if (index === total - 1) return 'pl-1.5 pr-4.5';
  return 'px-1.5';
}

function EmailCell({
  email,
  isOpen,
  statusIcon
}: {
  email: string | undefined;
  isOpen: boolean;
  statusIcon?: ReactNode;
}) {
  if (!email) return <span className="font-mono text-xs text-fg-subtle">—</span>;
  return (
    <span
      className={cn(
        'flex items-center gap-1.5 font-mono text-xs',
        isOpen ? 'text-warning' : 'text-fg-secondary'
      )}
    >
      <span className="truncate">{email}</span>
      {statusIcon}
    </span>
  );
}

export function DepartmentResolutionTable({
  departments,
  resolvedDepartments = [],
  managerAssignments,
  emailToUser,
  managerSuggestions,
  onAssignmentChange,
  isSubmitting = false
}: DepartmentResolutionTableProps) {
  const { t } = useTranslation();

  // Build combined display list: unresolved departments (editable), every
  // already-resolved department (read-only — resolutions stay visible instead
  // of vanishing from the page), and resolved ancestors for tree context.
  const displayDepartments = useMemo(() => {
    const unresolvedIds = new Set(departments.map((d) => d._id));

    // Find resolved ancestors: departments in resolvedDepartments whose ID
    // appears in some unresolved department's path (but isn't the dept itself)
    const ancestorIds = new Set<string>();
    for (const unresolvedDept of departments) {
      const pathSegments = unresolvedDept.path.split('#');
      // All segments except the last one (which is the dept itself) are ancestors
      for (let i = 0; i < pathSegments.length - 1; i++) {
        const ancestorId = pathSegments[i];
        if (!unresolvedIds.has(ancestorId)) {
          ancestorIds.add(ancestorId);
        }
      }
    }

    // Build display list
    const result: DisplayDepartment[] = [];

    // Departments with an assigned manager plus manager-less ancestors needed
    // for tree context; departments without any manager relationship don't
    // belong on this page.
    for (const dept of resolvedDepartments) {
      if (unresolvedIds.has(dept._id) || !dept.path) continue;
      if (dept.manager || ancestorIds.has(dept._id)) {
        result.push({ type: 'resolved', data: dept });
      }
    }

    // Add unresolved departments
    for (const dept of departments) {
      result.push({ type: 'unresolved', data: dept });
    }

    // Sort by path to maintain tree order
    return result.sort((a, b) => {
      const pathA = a.type === 'unresolved' ? a.data.path : a.data.path || '';
      const pathB = b.type === 'unresolved' ? b.data.path : b.data.path || '';
      return pathA.localeCompare(pathB);
    });
  }, [departments, resolvedDepartments]);

  // Helper to get tree prefix based on depth
  const getTreePrefix = (path: string): string => {
    const depth = path.split('#').length - 1;
    if (depth === 0) return '';
    return `${'  '.repeat(depth)}└ `; // Non-breaking spaces + └
  };

  const handleManagerSelect = (departmentId: string, managerId: string | string[] | undefined) => {
    if (Array.isArray(managerId)) return;
    onAssignmentChange(departmentId, managerId);
  };

  const resolvedCheck = (tooltipKey: string) => (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="flex-none cursor-help text-bliks-green-600">
          <Check className="h-3.5 w-3.5" strokeWidth={2.2} />
        </span>
      </TooltipTrigger>
      <TooltipContent>{t(tooltipKey)}</TooltipContent>
    </Tooltip>
  );

  const headerLabels = [
    t('bulkOnboarding.resolution.table.name'),
    t('bulkOnboarding.resolution.table.abbreviation'),
    t('bulkOnboarding.excel.columns.departments.description'),
    t('bulkOnboarding.resolution.table.managerEmail'),
    t('bulkOnboarding.resolution.table.assignManager')
  ];

  return (
    <TooltipProvider>
      <div className="overflow-hidden rounded-xl border border-line bg-white">
        <Table className="table-fixed">
          <TableHeader className="bg-surface-subtle [&_tr]:border-b-0">
            <TableRow className="border-b border-line hover:bg-transparent">
              {headerLabels.map((label, index) => (
                <TableHead
                  key={label}
                  style={{ width: COLUMN_WIDTHS[index] }}
                  className={cn(
                    'h-auto bg-transparent py-2.75 text-[11.5px] font-semibold text-muted-foreground',
                    cellPadding(index, headerLabels.length)
                  )}
                >
                  {label}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {displayDepartments.map((item) => {
              const cellClass = (index: number) => cn('py-3 align-middle', cellPadding(index, 5));

              if (item.type === 'resolved') {
                const dept = item.data;
                const managerName = dept.manager
                  ? `${dept.manager.firstName} ${dept.manager.lastName}`.trim()
                  : undefined;
                return (
                  <TableRow
                    key={dept._id}
                    className="border-b border-line-subtle hover:bg-transparent"
                  >
                    <TableCell className={cellClass(0)}>
                      <span className="text-[13px] font-semibold text-foreground">
                        <span className="font-mono text-xs text-fg-subtle">
                          {getTreePrefix(dept.path || '')}
                        </span>
                        {dept.name}
                      </span>
                    </TableCell>
                    <TableCell
                      className={cn(
                        cellClass(1),
                        'font-mono text-xs font-medium text-fg-secondary'
                      )}
                    >
                      {dept.abbreviation}
                    </TableCell>
                    <TableCell className={cn(cellClass(2), 'text-[12.5px] text-fg-secondary')}>
                      {dept.description || '—'}
                    </TableCell>
                    <TableCell className={cellClass(3)}>
                      <EmailCell
                        email={dept.manager?.email}
                        isOpen={false}
                        statusIcon={resolvedCheck(
                          'bulkOnboarding.resolution.table.alreadyResolved'
                        )}
                      />
                    </TableCell>
                    <TableCell className={cellClass(4)}>
                      {managerName ? (
                        <PersonChip
                          name={managerName}
                          subline={dept.manager?.mainRole?.name}
                          size={28}
                        />
                      ) : (
                        <span className="text-[12.5px] italic text-fg-subtle">
                          {t('bulkOnboarding.resolution.table.alreadyResolved')}
                        </span>
                      )}
                    </TableCell>
                  </TableRow>
                );
              }

              const dept = item.data;

              const hasEmailMatch = !!managerEmailMatch(dept, emailToUser);
              const managerId = effectiveManagerId(dept, managerAssignments, emailToUser);
              const isOpen = !managerId;

              const suggestion = isOpen ? managerSuggestions?.get(dept._id) : undefined;

              const statusIcon: ReactNode = hasEmailMatch
                ? resolvedCheck('bulkOnboarding.resolution.table.managerFound')
                : undefined;

              return (
                <TableRow
                  key={dept._id}
                  className="border-b border-line-subtle hover:bg-transparent"
                >
                  <TableCell className={cellClass(0)}>
                    <span className="text-[13px] font-semibold text-foreground">
                      <span className="font-mono text-xs text-fg-subtle">
                        {getTreePrefix(dept.path)}
                      </span>
                      {dept.name}
                    </span>
                  </TableCell>
                  <TableCell
                    className={cn(cellClass(1), 'font-mono text-xs font-medium text-fg-secondary')}
                  >
                    {dept.abbreviation}
                  </TableCell>
                  <TableCell className={cn(cellClass(2), 'text-[12.5px] text-fg-secondary')}>
                    {dept.description || (
                      <span className="italic text-fg-subtle">
                        {t('bulkOnboarding.resolution.table.noDescription')}
                      </span>
                    )}
                  </TableCell>
                  <TableCell className={cellClass(3)}>
                    <EmailCell
                      email={dept.managerEmail}
                      isOpen={!hasEmailMatch}
                      statusIcon={statusIcon}
                    />
                  </TableCell>
                  <TableCell className={cellClass(4)}>
                    <UserSelect
                      value={managerId || undefined}
                      onChange={(value) => handleManagerSelect(dept._id, value)}
                      placeholder={t('bulkOnboarding.assignManager.selectPlaceholder')}
                      disabled={isSubmitting}
                      settings={{ compactTrigger: true }}
                      className={cn(
                        'rounded-[9px] text-[12.5px]',
                        isOpen && 'border-warning-solid bg-warning-solid/6'
                      )}
                    />
                    {suggestion && (
                      <div className="mt-1.5 flex items-center gap-2 text-xs text-fg-secondary">
                        <span className="truncate">
                          {t('initialSetup.resolve.suggestion', { name: suggestion.displayName })}
                        </span>
                        <button
                          type="button"
                          disabled={isSubmitting}
                          onClick={() => handleManagerSelect(dept._id, suggestion.user._id)}
                          className="flex-none font-semibold text-positive hover:underline disabled:opacity-50"
                        >
                          {t('initialSetup.resolve.applySuggestion')}
                        </button>
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </TooltipProvider>
  );
}
